import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { getSettings, updateSettings } from "../utils/apiClient";
import { VISIBILITY_OPTIONS } from "../post/compositions";

const TOPICS = [
  { key: "home", label: "Hjem", hint: "Journal-feed med indlæg" },
  { key: "categories", label: "Kategorier", hint: "Tags og filtrering" },
  { key: "recipes", label: "Opskrifter", hint: "Opskriftsliste og detalje" },
];

const LEVELS = VISIBILITY_OPTIONS.map((opt) => opt.value);

const EMPTY_MATRIX = {
  home: [...LEVELS],
  categories: [...LEVELS],
  recipes: [...LEVELS],
};

function TopicVisibilityPanel() {
  const [matrix, setMatrix] = useState(EMPTY_MATRIX);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getSettings();
        if (cancelled) return;
        setMatrix({
          home: data?.topic_visibility?.home || [...LEVELS],
          categories: data?.topic_visibility?.categories || [...LEVELS],
          recipes: data?.topic_visibility?.recipes || [...LEVELS],
        });
      } catch (err) {
        if (!cancelled) {
          const detail = err.response?.data?.detail;
          setError(typeof detail === "string" ? detail : err.message || "Failed to load settings");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const isChecked = (topic, level) => (matrix[topic] || []).includes(level);

  const toggle = (topic, level) => {
    setSaved(false);
    setMatrix((prev) => {
      const current = new Set(prev[topic] || []);
      if (current.has(level)) {
        current.delete(level);
      } else {
        current.add(level);
      }
      if (current.size === 0) current.add("private");
      const ordered = LEVELS.filter((l) => current.has(l));
      return { ...prev, [topic]: ordered };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const data = await updateSettings({ topic_visibility: matrix });
      setMatrix({
        home: data?.topic_visibility?.home || [...LEVELS],
        categories: data?.topic_visibility?.categories || [...LEVELS],
        recipes: data?.topic_visibility?.recipes || [...LEVELS],
      });
      setSaved(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Emnesynlighed
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 640 }}>
        Loft for Hjem, Kategorier og Opskrifter. Ikke-markerede niveauer skjules for alle
        undtagen forfatter og admin, og fjernes fra opret/rediger-formularer.
      </Typography>

      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ overflow: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Emne</TableCell>
                {VISIBILITY_OPTIONS.map((opt) => (
                  <TableCell key={opt.value} align="center" sx={{ fontWeight: 700 }}>
                    {opt.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {TOPICS.map((topic) => (
                <TableRow key={topic.key} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 650 }}>
                      {topic.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {topic.hint}
                    </Typography>
                  </TableCell>
                  {LEVELS.map((level) => (
                    <TableCell key={level} align="center">
                      <Checkbox
                        checked={isChecked(topic.key, level)}
                        onChange={() => toggle(topic.key, level)}
                        inputProps={{
                          "aria-label": `${topic.label} ${level}`,
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
        <Button variant="contained" onClick={handleSave} disabled={loading || saving}>
          {saving ? "Gemmer…" : "Gem indstillinger"}
        </Button>
        {saved ? (
          <Typography variant="body2" color="success.main">
            Gemt
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}

export default TopicVisibilityPanel;
